import { gql } from '@apollo/client'

/**
 * @typedef {Object} PMScreenGraphQLResponse
 * @property {string} id The primary identifier for this record
 * @property {string} screenType Screen type string constant that identifies the
 *   screen/page. Common types: 'member-home' (main member landing page, automatically
 *   upgraded to v2), 'member-home-v2' (member home version 2), 'member-home-v4'
 *   (member home version 4 with experiments), 'centers' (centers/communities page)
 * @property {string} language Two letter language code (e.g., 'en', 'es', 'fr')
 *   for the screen's language
 * @property {string} createdAt The date the row was created
 * @property {string} updatedAt The date the row was last updated
 * @property {Array<{id: string}>} [pmSections] PM Sections which are children of
 *   this PM Screen. Returns all sections belonging to this screen, ordered by the
 *   section's 'order' field
 */

/**
 * @typedef {Object} PMScreenPersonalizedResponseGraphQLResponse
 * @property {PMScreenGraphQLResponse} [pmScreen] The personalized PM Screen null
 *   when the screen is not found
 * @property {string} screenType The screen type for the personalized screen, this
 *   is inferred screen type from the requested args (e.g., the value used to get
 *   the pmScreen)
 */

const GET_PM_SCREEN_PERSONALIZED = gql`
  query GetPmScreenPersonalized($screenType: String!, $language: [String], $sectionsLimit: Int) {
    pmScreenPersonalized(screenType: $screenType, language: $language) {
      pmScreen {
        id
        screenType
        language
        # All sections - only need id for sectionIds in pmScreen
        # Personalization is applied automatically by pmScreenPersonalized resolver
        # and when fetching section data via app field
        pmSections(limit: 300) {
          id
        }
        # Limited sections with full details (app data and pmLists) for initial screen loading
        # This alias minimizes API requests by fetching section details in the same query
        pmSectionsWithDetails: pmSections(limit: $sectionsLimit) {
          id
          type
          adminTitle
          visibility {
            type
            inverse
            content {
              contentType
              contentId
            }
            subscriptions
            threshold
          }
          app(LASpotlightOption: true, LASpotlightTypes: ["LIVE", "AMA"]) {
            id
            type
            adminTitle
            enabled
            visibility {
              type
              inverse
              content {
                contentType
                contentId
              }
              subscriptions
              threshold
            }
            data {
              ... on PMSectionAppDataContentList {
                contextType
                displayMode
                enableContentPreferencesFiltering
                enableUserHiddenContent
                logSectionVisibility
                hideWatched
                sortOrder
                recommId
                footer {
                  label
                }
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                  }
                }
                header {
                  label
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
                content {
                  listId
                  label
                }
              }
              ... on PMSectionAppDataContinueWatching {
                title
                description
                header {
                  label
                }
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                  }
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
              }
              ... on PMSectionAppDataPlacement {
                placement
                contextType
                tabs {
                  tabLabel
                  merchEventId
                  viewMode
                  slug
                  payloadType
                  pineScore
                  pineSource
                  tabContent {
                    contentId
                    contentType
                    label
                    seedQuestion {
                      id
                      question
                      language
                      tags
                    }
                    pineSource
                    pineScore
                    videoTasteSegment
                    title
                    subTitle
                    description
                    mobileBackgroundUrl
                    countdown
                    eventType
                    webviewUrl
                  }
                }
                render {
                  type
                  background
                  tileCount
                  link
                }
              }
              ... on PMSectionAppDataHeaderSection {
                imageId
                imageUrl
                title
                description
                asset {
                  id
                  renderUrl
                }
              }
            }
            provider
            providerKey
            clientAttributes
            availabilityClientAttributes
            createdAt
            updatedAt
            order
            pmScreenId
          }
          pmList(limit: 100) {
            id
            type
            language
            adminTitle
            title
            description
            listItems {
              type
              id
              imageId
              imageUrl
              title
              childListId
            }
            createdAt
            updatedAt
          }
        }
      }
      screenType
    }
  }
`

export default GET_PM_SCREEN_PERSONALIZED
